/**
 * CORE-1791 — общие детали материала v3: контур выбранности и доступ к токенам.
 */
import React, { useMemo } from 'react';
import { Platform, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import { useTheme } from '../../context/ThemeContext';
import { useMediaQuery } from '../live/useMediaQuery';
import {
  MESSAGES_V3, UNREAD_CAPSULE, contourRingWebStyle, messagesV3Geometry, unreadLabel,
  type MessagesV3Geometry, type MessagesV3Tokens,
} from './messagesV3';

export interface MessagesV3 {
  t: MessagesV3Tokens;
  g: MessagesV3Geometry;
  dark: boolean;
  phone: boolean;
  reduceMotion: boolean;
}

export function useMessagesV3(layout: 'phone' | 'compact' | 'wide'): MessagesV3 {
  const { isDark } = useTheme();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  return useMemo(() => ({
    t: MESSAGES_V3[isDark ? 'dark' : 'light'],
    g: messagesV3Geometry(layout),
    dark: isDark,
    phone: layout === 'phone',
    reduceMotion,
  }), [isDark, layout, reduceMotion]);
}

/**
 * Фирменный контур 1px отдельным слоем: градиент рисуется в рамке шириной 1px
 * (маска вычитает внутренность), поэтому не ложится на текст и не меняет
 * размеры элемента. Слой не ловит нажатия.
 */
export const ContourRing: React.FC<{ radius: number; opacity: number; testID?: string; width?: number }> = ({ radius, opacity, testID, width = 1 }) => {
  if (Platform.OS !== 'web') return null;
  return (
    <View
      pointerEvents="none"
      testID={testID}
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: radius, padding: width, opacity,
        ...({
          ...contourRingWebStyle(),
          transitionProperty: 'opacity', transitionDuration: '160ms',
        } as object),
      }}
    />
  );
};

/**
 * Счётчик непрочитанных v3 — капсула вместо оранжевой плашки: внутренняя
 * заливка кольца, цифра акцентным токеном (7.8:1 / 6.5:1), контур градиентом
 * 1.5px. Сама капсула для скринридера скрыта: точное число (а не «99+»)
 * произносит доступное имя строки, в которой она стоит.
 */
export const UnreadCapsule: React.FC<{ count: number; t: MessagesV3Tokens; style?: StyleProp<ViewStyle>; testID?: string }> = ({ count, t, style, testID }) => (
  <View
    testID={testID}
    accessibilityElementsHidden
    importantForAccessibility="no-hide-descendants"
    {...({ 'aria-hidden': true } as object)}
    style={[
      {
        position: 'relative', height: UNREAD_CAPSULE.height, minWidth: UNREAD_CAPSULE.minWidth,
        paddingHorizontal: UNREAD_CAPSULE.padH, borderRadius: UNREAD_CAPSULE.radius,
        backgroundColor: t.inner, alignItems: 'center', justifyContent: 'center',
      },
      style,
    ]}
  >
    <Text
      style={{
        color: t.accent, fontSize: 12, lineHeight: 16, fontWeight: '600', fontVariant: ['tabular-nums'],
        fontFamily: 'var(--ag-font-ui)',
      } as any}
    >
      {unreadLabel(count)}
    </Text>
    <ContourRing radius={UNREAD_CAPSULE.radius} opacity={1} width={UNREAD_CAPSULE.contour} />
  </View>
);
